import { motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Shield,
  Lock,
  Globe,
  Activity,
  Database,
  Eye,
  EyeOff,
  RefreshCw,
  Terminal,
  Settings,
  BarChart3,
  Plus,
  Pencil,
  Trash2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { adminSecurityApi } from '../../../shared/services/adminSecurityApi'
import AdminHeader from '../../../shared/components/AdminHeader'
import SecurityRuleModal from '../components/modals/SecurityRuleModal'
import type { SecurityRule } from '../components/modals/SecurityRuleModal'

interface SecurityMetrics {
  totalThreats: number
  blockedRequests: number
  activeSecurityRules: number
  lastSecurityScan: string
  systemIntegrity: number
  encryptionStatus: 'enabled' | 'disabled'
  sslCertStatus: 'valid' | 'expired' | 'expiring_soon'
  backupStatus: 'healthy' | 'failed' | 'in_progress'
}

const TYPE_LABELS: Record<string, string> = {
  rate_limit: 'Rate Limit',
  ip_block: 'IP Block',
  pattern_match: 'Pattern',
  geo_block: 'Geo Block',
  malware_detection: 'Malware',
  phishing_protection: 'Phishing',
}

const ACTION_COLORS: Record<string, string> = {
  block: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  warn: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  log: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  redirect:
    'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
}

export default function SecuritySettingsPage() {
  const [isLoading, setIsLoading] = useState(true)
  const [metrics, setMetrics] = useState<SecurityMetrics | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [securityRules, setSecurityRules] = useState<SecurityRule[]>([])
  const [selectedTab, setSelectedTab] = useState<
    'overview' | 'rules' | 'settings'
  >('overview')
  const [showApiKey, setShowApiKey] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<SecurityRule | null>(null)
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null)

  useEffect(() => {
    loadSecurityData()

    // Set up interval for real-time security updates (every 30 seconds)
    const securityUpdateInterval = setInterval(async () => {
      try {
        setMetrics((await adminSecurityApi.metrics()) as SecurityMetrics)
      } catch (error) {
        console.error('Failed to update security metrics:', error)
      }
    }, 30000) // 30 seconds

    // Clean up interval on component unmount
    return () => {
      clearInterval(securityUpdateInterval)
    }
  }, [])

  const loadSecurityData = async () => {
    setIsLoading(true)
    try {
      const [metricsData, rulesData] = await Promise.all([
        adminSecurityApi.metrics(),
        adminSecurityApi.rules(),
      ])

      setMetrics(metricsData as SecurityMetrics)
      setSecurityRules(rulesData as SecurityRule[])
      setLastUpdated(new Date())
      setLoadError(null)
    } catch (error) {
      console.error('Failed to load security data:', error)
      toast.error('Failed to load security data')

      // 예전에는 여기서 그럴듯한 숫자(위협 147건·무결성 98.5%·SSL 정상)로
      // 대체했다. 보안 지표를 못 읽어 온 상황에서 화면은 "전부 정상"으로
      // 보였다. 읽지 못했으면 읽지 못했다고 보여준다.
      setMetrics(null)
      setSecurityRules([])
      setLoadError(
        error instanceof Error ? error.message : 'Failed to fetch security data'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleRuleToggle = async (ruleId: string, enabled: boolean) => {
    try {
      await adminSecurityApi.toggleRule(ruleId, enabled)

      setSecurityRules(prev =>
        prev.map(rule => (rule._id === ruleId ? { ...rule, enabled } : rule))
      )

      toast.success(`Security rule ${enabled ? 'enabled' : 'disabled'}`)
    } catch (error) {
      console.error('Failed to update security rule:', error)
      toast.error('Failed to update security rule')
    }
  }

  const handleDeleteRule = async (ruleId: string) => {
    try {
      await adminSecurityApi.deleteRule(ruleId)

      setSecurityRules(prev => prev.filter(rule => rule._id !== ruleId))
      setDeletingRuleId(null)
      toast.success('Security rule deleted')
    } catch (error) {
      console.error('Failed to delete security rule:', error)
      toast.error('Failed to delete security rule')
    }
  }

  const openCreateModal = () => {
    setEditingRule(null)
    setIsModalOpen(true)
  }

  const openEditModal = (rule: SecurityRule) => {
    setEditingRule(rule)
    setIsModalOpen(true)
  }

  const handleForceSecurityScan = async () => {
    try {
      setIsLoading(true)
      const result = { data: await adminSecurityApi.scan() }

      setMetrics(prev =>
        prev
          ? {
              ...prev,
              lastSecurityScan: result.data.scanTime,
              systemIntegrity: result.data.systemIntegrity,
            }
          : null
      )

      toast.success('Security scan completed')
    } catch (error) {
      console.error('Security scan failed:', error)
      toast.error('Security scan failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminHeader
        title="Security Settings"
        subtitle="Manage system security, monitoring, and threat protection"
        showRefresh={true}
        onRefresh={loadSecurityData}
        isRefreshing={isLoading}
        lastUpdated={lastUpdated}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Tab Navigation */}
        <div className="mb-8 border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', name: 'Security Overview', icon: Shield },
              { id: 'rules', name: 'Security Rules', icon: Settings },
              { id: 'settings', name: 'Advanced Settings', icon: Terminal },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id as any)}
                className={`flex items-center whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${
                  selectedTab === tab.id
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                <tab.icon className="mr-2 h-4 w-4" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {selectedTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            {loadError && (
              <div
                role="alert"
                className="rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/40"
              >
                <p className="font-medium text-red-800 dark:text-red-200">
                  보안 지표를 불러오지 못했습니다
                </p>
                <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                  아래 값은 비어 있습니다. 현재 상태를 반영하지 않으므로 정상
                  여부를 판단하는 근거로 쓰지 마세요. ({loadError})
                </p>
              </div>
            )}

            {/* Security Metrics Grid */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Shield className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                          Threats Blocked
                        </dt>
                        <dd className="flex items-center justify-between">
                          <span className="text-lg font-medium text-gray-900 dark:text-white">
                            {metrics?.totalThreats.toLocaleString()}
                          </span>
                          <Link
                            to="/security/logs?type=threat_blocked"
                            className="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                          >
                            View Details
                          </Link>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Activity className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                          Blocked Requests
                        </dt>
                        <dd className="flex items-center justify-between">
                          <span className="text-lg font-medium text-gray-900 dark:text-white">
                            {metrics?.blockedRequests.toLocaleString()}
                          </span>
                          <Link
                            to="/security/logs?type=request_blocked"
                            className="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                          >
                            View Details
                          </Link>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Settings className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                          Active Rules
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {metrics?.activeSecurityRules}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <BarChart3 className="h-6 w-6 text-orange-600" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                          System Integrity
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {metrics?.systemIntegrity}%
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* System Status */}
            <div className="rounded-lg bg-white shadow dark:bg-gray-800">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="mb-6 text-lg font-medium leading-6 text-gray-900 dark:text-white">
                  System Security Status
                </h3>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div className="flex items-center justify-between rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
                    <div className="flex items-center">
                      <Lock className="mr-3 h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-green-900 dark:text-green-100">
                        Encryption
                      </span>
                    </div>
                    <span className="text-sm capitalize text-green-700 dark:text-green-300">
                      {metrics?.encryptionStatus}
                    </span>
                  </div>

                  <div className="flex items-center justify-between rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
                    <div className="flex items-center">
                      <Globe className="mr-3 h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-green-900 dark:text-green-100">
                        SSL Certificate
                      </span>
                    </div>
                    <span className="text-sm capitalize text-green-700 dark:text-green-300">
                      {metrics?.sslCertStatus}
                    </span>
                  </div>

                  <div className="flex items-center justify-between rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
                    <div className="flex items-center">
                      <Database className="mr-3 h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-green-900 dark:text-green-100">
                        Backup Status
                      </span>
                    </div>
                    <span className="text-sm capitalize text-green-700 dark:text-green-300">
                      {metrics?.backupStatus}
                    </span>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Last security scan:{' '}
                    {metrics?.lastSecurityScan
                      ? new Date(metrics.lastSecurityScan).toLocaleString()
                      : 'Never'}
                    {lastUpdated && (
                      <span className="ml-4 text-xs">
                        Data updated: {lastUpdated.toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleForceSecurityScan}
                    disabled={isLoading}
                    className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                  >
                    <RefreshCw
                      className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                    />
                    Force Security Scan
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Security Rules Tab */}
        {selectedTab === 'rules' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="rounded-lg bg-white shadow dark:bg-gray-800"
          >
            <div className="px-4 py-5 sm:p-6">
              <div className="mb-6 flex items-center justify-between">
                <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                  Security Rules Configuration
                </h3>
                <button
                  onClick={openCreateModal}
                  className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4" />
                  Add Rule
                </button>
              </div>
              <div className="space-y-3">
                {securityRules.map(rule => (
                  <div
                    key={rule._id}
                    className="rounded-lg border border-gray-200 p-4 dark:border-gray-600"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-center gap-2">
                          <h4 className="truncate text-sm font-medium text-gray-900 dark:text-white">
                            {rule.name}
                          </h4>
                          <span className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                            {TYPE_LABELS[rule.type] || rule.type}
                          </span>
                          <span
                            className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${ACTION_COLORS[rule.action] || 'bg-gray-100 text-gray-600'}`}
                          >
                            {rule.action}
                          </span>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            P{rule.priority ?? 0}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {rule.description}
                        </p>
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2">
                        <button
                          onClick={() => openEditModal(rule)}
                          className="p-1.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                          title="Edit rule"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {deletingRuleId === rule._id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDeleteRule(rule._id)}
                              className="rounded bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeletingRuleId(null)}
                              className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeletingRuleId(rule._id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                            title="Delete rule"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                        <label className="relative ml-2 inline-flex cursor-pointer items-center">
                          <input
                            type="checkbox"
                            checked={rule.enabled}
                            onChange={e =>
                              handleRuleToggle(rule._id, e.target.checked)
                            }
                            className="peer sr-only"
                          />
                          <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-indigo-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-indigo-800"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
                {securityRules.length === 0 && (
                  <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No security rules configured.
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Security Rule Modal */}
        <SecurityRuleModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          rule={editingRule}
          onSaved={loadSecurityData}
        />

        {/* Advanced Settings Tab */}
        {selectedTab === 'settings' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* API Security */}
            <div className="rounded-lg bg-white shadow dark:bg-gray-800">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900 dark:text-white">
                  API Security
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        API Key
                      </label>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Secure API key for system administration
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <code className="rounded bg-gray-100 px-3 py-1 font-mono text-sm dark:bg-gray-700">
                        {showApiKey
                          ? 'sk_live_abc123xyz789...'
                          : '••••••••••••••••'}
                      </code>
                      <button
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showApiKey ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Backup Configuration */}
            <div className="rounded-lg bg-white shadow dark:bg-gray-800">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900 dark:text-white">
                  Backup & Recovery
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Automatic Backups
                      </label>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Daily encrypted backups to secure storage
                      </p>
                    </div>
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        defaultChecked
                        className="peer sr-only"
                      />
                      <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-indigo-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-indigo-800"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}
